import type { AxiosProgressEvent } from 'axios';
import { apiClient } from './client';
import type { CompressionLevel, MergeManifest, SplitManifest } from './types';

const PDF_TOOL_TIMEOUT = 5 * 60 * 1000; // 5 минут, см. API.md: merge может быть долгим.

export interface PdfToolCallOptions {
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

async function postBlob(
  url: string,
  form: FormData,
  options: PdfToolCallOptions,
): Promise<Blob> {
  const response = await apiClient.post<Blob>(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
    timeout: PDF_TOOL_TIMEOUT,
    onUploadProgress: options.onUploadProgress,
    signal: options.signal,
  });
  return response.data;
}

export async function compressPdf(
  files: File[],
  level: CompressionLevel,
  options: PdfToolCallOptions = {},
): Promise<Blob> {
  const form = new FormData();
  files.forEach((f) => form.append('files[]', f));
  form.append('manifest', JSON.stringify({ level }));
  return postBlob('/api/pdf/compress', form, options);
}

// Важно: endpoint принимает ОДИН файл в поле `file`, а не `files[]`.
export async function splitPdf(
  file: File,
  manifest: SplitManifest,
  options: PdfToolCallOptions = {},
): Promise<Blob> {
  const form = new FormData();
  form.append('file', file);
  form.append('manifest', JSON.stringify(manifest));
  return postBlob('/api/pdf/split', form, options);
}

// Merge: минимум 2 PDF-файла. Manifest содержит повороты по индексу файла
// (string-ключ, 0-based), порядок задаётся порядком files[] в FormData.
export async function mergePdf(
  files: File[],
  manifest: MergeManifest,
  options: PdfToolCallOptions = {},
): Promise<Blob> {
  const form = new FormData();
  files.forEach((f) => form.append('files[]', f));
  form.append('manifest', JSON.stringify(manifest));
  return postBlob('/api/pdf/merge', form, options);
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Достаёт имя файла из Content-Disposition (бэк отдаёт его, см. API.md).
export function filenameFromBlobResponse(
  blob: Blob,
  fallback: string,
  contentDisposition?: string,
): string {
  if (contentDisposition) {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  if (blob.type === 'application/zip') {
    return `${fallback}.zip`;
  }
  return fallback;
}
